<template>
    <div>
        <b-media
            :id="`key-result-row-${keyResult.id}`"
            v-show="showKeyResultsDetails"
            @click="showInitiatives = !showInitiatives"
            no-body
            class="border-start-key-result mb-2 mt-2"
        >
            <b-media-aside vertical-align="center" class="user-picture mr-0">
                <UserPicture :user="keyResult.user" />
            </b-media-aside>
            <b-container fluid tag="b-media-body" class="p-0">
                <b-row no-gutters align-v="center">
                    <b-col lg="12" xl="5" class="p-0 collapse-toggle">
                        <b-row no-gutters>
                            <b-col class="col-11">
                                <div>
                                    {{ keyResult.title }}
                                </div>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col lg="12" xl="7" class="mt-2 mt-xl-0 p-0">
                        <b-row class="kr data">
                            <div class="group-items w-100 alignment-fix">
                                <b-link :href="goToKeyResultPage(keyResult.objective_id, keyResult.id)">
                                    <img src="@/assets/icons/arrow-up-right.svg" />
                                </b-link>
                                <ProgressBar
                                    :value="calculateKeyResultProgress(keyResult.progress)"
                                    height="1rem"
                                    use-default-label
                                    class="progress"
                                />
                                <LastCheckInDetails :key-result="keyResult" />
                            </div>
                            <div class="initiative status-counter">
                                <StatusCounter
                                    v-if="hasDaily"
                                    :statusCounter="statusCounter"
                                    :idTextAppend="`key-result-${keyResult.id}`"
                                />
                            </div>
                            <div class="group-items actions-btns">
                                <CheckInBtn
                                    class="checkin"
                                    :id="`btn-strategy-new-checkin-${keyResult.id}`"
                                    @click="onClickCheckIn(keyResult.objective_id, keyResult.id)"
                                    :disabled="hasNotCheckinPermission()"
                                />
                                <Dropdown
                                    class="dropdown"
                                    :id="`dropdown-actions-of-key-result-${keyResult.id}`"
                                    :on-edit-callback="onEdit(keyResult.objective_id, keyResult.id)"
                                    :on-delete-callback="onDelete(keyResult.objective_id, keyResult.id)"
                                    :on-move-callback="onMove(keyResult.objective_id, keyResult.id)"
                                    :can-move="canMoveKR"
                                    :disabled-tooltip-label="$t('hasNotAuthorization')"
                                    :disabled="notAllowedToPerformActions()"
                                    :has-not-edit-permission="hasNotEditPermission()"
                                    :has-not-move-permission="hasNotEditPermission()"
                                    :has-not-delete-permission="hasNotDeletePermission()"
                                />
                            </div>
                        </b-row>
                    </b-col>
                </b-row>
            </b-container>
        </b-media>

        <b-row :class="showKeyResultsDetails ? '' : 'mt-1'">
            <b-col :class="showKeyResultsDetails ? 'ml-4' : ''">
                <b-collapse :visible="true" v-model="showInitiativesCollapse" id="collapse">
                    <b-media>
                        <Initiatives
                            v-if="showInitiativesDetails"
                            :initiatives="keyResult.initiatives"
                            :empty-text="$t('isEmptyAlert')"
                            :krId="keyResult.id"
                            :okriFilter="okriFilter"
                            :initiativesFilter="initiativesFilter"
                            @onChangeInitiative="$emit('onChangeInitiative')"
                            :periodStatus="periodStatus"
                            @statusCounter="($event) => statusCounterHandler($event)"
                        />
                    </b-media>
                </b-collapse>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import ProgressBar from '@/views/components/progress-bar/ProgressBar.vue'
import { UserPicture, Dropdown, CheckInBtn, LastCheckInDetails } from '@/views/okr/components'
import { deleteKeyResultFromAPI } from '@/views/okr/key-result/details/api.js'
import { getV1PathWithOrigin } from '@/router/routesToV1'
import { danger, success } from '@/lib/notification'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import commonMessages from '@/views/okr/common-messages'
import { userPermissionsMixin } from '@/mixins'
import StatusCounter from '@/views/okr/daily/statusCounter/StatusCounter.vue'
import { includes } from 'lodash'
import { mapState } from 'vuex'

export default {
    name: 'DailyKeyResultDetails',
    mixins: [userPermissionsMixin],
    components: {
        Initiatives: () => import('@/views/okr/daily/initiatives/Initiatives.vue'),
        CheckInBtn,
        ProgressBar,
        UserPicture,
        Dropdown,
        StatusCounter,
        LastCheckInDetails,
    },
    props: {
        keyResult: {
            type: Object,
            required: true,
        },
        objective: {
            type: Object,
            required: true,
        },
        okriFilter: {
            type: Array,
        },
        initiativesFilter: {
            type: Array,
        },
        periodStatus: {
            type: String,
        },
        expandAllToggle: {
            type: Boolean,
        },
    },
    watch: {
        expandAllToggle(val) {
            this.showInitiatives = val
        },
    },
    data() {
        return {
            showInitiatives: false,
            statusCounter: {},
        }
    },
    computed: {
        ...mapState('profile', ['user']),
        hasDaily() {
            return includes(this.user.has_modules, 'has_daily')
        },
        showKeyResultsDetails() {
            return this.okriFilter.includes('key-results')
        },
        showInitiativesCollapse: {
            get() {
                return this.showKeyResultsDetails ? this.showInitiatives : true
            },
            set(val) {
                return val
            },
        },
        showInitiativesDetails() {
            return this.okriFilter.includes('initiatives')
        },
        canMoveKR() {
            const { currentUser, keyResult, objective } = this
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_okr_leader']

            return (
                this.userHasPermission(currentUser, availableRoles, []) ||
                this.isOwnerOfKeyResult(keyResult.user.id, currentUser.id) ||
                this.isManagerOfKeyResult(keyResult.user.manager_id, currentUser.manager_id) ||
                this.isOwnerOfObjetive(objective.user.id, currentUser.id) ||
                this.isManagerOfObjective(objective.user.manager_id, currentUser.manager_id)
            )
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        keyResultPermissions() {
            return this.objective.key_results_permissions.find(
                (kr_permission) => kr_permission.key_result_id == this.keyResult.id
            )
        },
    },
    methods: {
        statusCounterHandler(event) {
            if (event) {
                this.statusCounter = event
                this.$emit('statusCounter', event)
            }
        },
        onClickCheckIn(objectiveId, keyResultId) {
            if (!this.$route.path.includes('checkin')) {
                this.$router.push({
                    path: `${this.$route.path}/objectiveId/${objectiveId}/resultId/${keyResultId}/checkin`,
                })
            }
        },
        goToKeyResultPage(objectiveId, keyResultId) {
            return `${getV1PathWithOrigin('/okr/objectives/key-results', {
                objectiveId: objectiveId,
                keyResultId: keyResultId,
            })}`
        },
        onEdit(objectiveId, keyResultId) {
            return () => {
                this.$router.push({
                    path: `${this.$route.path}/objectiveId/${objectiveId}/resultId/${keyResultId}/edit`,
                })
            }
        },
        onDelete(objectiveId, keyResultId) {
            return () => {
                proceedWithConfirmation({
                    type: 'warning',
                    icon: 'warning',
                    message: this.$t('deletionAlert.message'),
                    confirmButtonText: this.$t('deletionAlert.textButtonConfirmation'),
                    onConfirm: () => {
                        return deleteKeyResultFromAPI(objectiveId, keyResultId)
                    },
                    callback: () => {
                        success(this.$t('deletionAlert.onSuccess'))
                        this.onDeletedKeyResult()
                    },
                    onError: () => {
                        danger(this.$t('deletionAlert.onError'))
                    },
                })
            }
        },
        onDeletedKeyResult() {
            this.$emit('onDeletedKeyResult')
        },
        onMove(objectiveId, keyResultId) {
            return () => {
                this.$router.push({
                    path: `${this.$route.path}/objectiveId/${objectiveId}/resultId/${keyResultId}/move`,
                })
            }
        },
        calculateKeyResultProgress(progress) {
            return progress.toFixed(2) * 100
        },
        isOwnerOfKeyResult(user, currentUser) {
            return user.id === currentUser.id
        },
        isManagerOfKeyResult(user, currentUser) {
            return user.manager_id === currentUser.manager_id
        },
        isOwnerOfObjetive(user, currentUser) {
            return user.id === currentUser.id
        },
        isManagerOfObjective(user, currentUser) {
            return user.manager_id === currentUser.manager_id
        },
        hasNotEditPermission() {
            return !this.keyResultPermissions.has_update_permission
        },
        hasNotDeletePermission() {
            return !this.keyResultPermissions.has_delete_permission
        },
        hasNotCheckinPermission() {
            return !this.keyResultPermissions.has_checkin_permission
        },
        notAllowedToPerformActions() {
            return !this.keyResultPermissions.has_update_permission && !this.keyResultPermissions.has_delete_permission
        },
    },
    i18n: {
        messages: {
            'en-US': {
                deletionAlert: {
                    message: 'You will no longer be able to retrieve this key result.',
                    textButtonConfirmation: 'Yes, delete',
                    onSuccess: 'Key result deleted successfully.',
                    onError: 'Ops... Something went wrong while deleting the key result.',
                },
                isEmptyAlert: 'This key result has no defined initiatives.',
                hasNotAuthorization: 'You are not authorized to perform actions on this Key Result',
            },
            'en-ES': {
                deletionAlert: {
                    message: 'Ya no podrá recuperar este resultado clave.',
                    textButtonConfirmation: 'Si, eliminar',
                    onSuccess: 'El resultado clave se eliminó con éxito.',
                    onError: 'Vaya, algo salió mal al eliminar el resultado clave.',
                },
                isEmptyAlert: 'Este resultado clave no tiene iniciativas definidas.',
                hasNotAuthorization: 'No está autorizado para realizar acciones en este Resultado Clave',
            },
            'pt-BR': {
                deletionAlert: {
                    message: 'Você não poderá mais recuperar este resultado chave.',
                    textButtonConfirmation: 'Sim, excluir',
                    onSuccess: 'Resultado chave excluído com sucesso.',
                    onError: 'Ops, algo deu errado ao excluir o resultado chave.',
                },
                isEmptyAlert: 'Este resultado chave não possui iniciativas definidas.',
                hasNotAuthorization: 'Você não tem autorização para realizar ações neste Resultado Chave',
            },
        },
        sharedMessages: commonMessages,
    },
}
</script>
<style scoped>
.collapse-toggle {
    width: 10% !important;
}

::v-deep .border-start-key-result {
    border-left: 4px solid #009ada !important;
}

::v-deep .border-start-key-result:hover {
    background: #ececec;
}

::v-deep .data {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
}

.data .group-items {
    display: flex;
    gap: 0.8rem;
    align-items: center;
}

::v-deep .data .w-100 {
    width: 65% !important;
}
::v-deep .collapse-toggle {
    cursor: pointer;
}

.user-picture:hover {
    cursor: pointer;
}
</style>
